import DateUtils from '@/utils/dateUtils'
import StringUtils from '@/utils/stringUtils'
import GeolocationUtils from '@/utils/geolocationUtils'
import FileReaderUtils from '@/utils/fileReaderUtils'
import EnvironmentUtils from '@/utils/environmentUtils'
import Config from '~/config'
import pdfMake from 'pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts';

pdfMake.vfs = pdfFonts.pdfMake.vfs

function horizontalLine (options)
{
    if (!options.thickness) options.thickness = 0.5
    if (!options.margin) options.margin = []
    
    return {canvas: [{ type: 'line', x1: 0, y1: 5, x2: 595-2*40, y2: 5, lineWidth: options.thickness }], margin: options.margin}
}

function separator (height)
{
    return { text: '', margin: [0, 0, 0, height] }
}

async function generatePDFDocument ({title, bodyContent, footerPageCountAdditionalText} = {})
{
    const doc =
    {
        info:
        {
            title,
            author: EnvironmentUtils.cityName
        },
        pageSize: 'A4',
        pageOrientation: 'portrait',
        
        content:
        [
            bodyContent
        ],
        
        footer (currentPage, pageCount)
        {
            return [
                {
                    text: ['Généré depuis l\'application ', {text: EnvironmentUtils.cityName, style: 'link', link: Config.appWebsiteUrl}],
                    alignment: 'right',
                    margin: [0, 0, 30, 0],
                    style: 'footer'
                },
                {
                    text: `Page ${currentPage} sur ${pageCount}${footerPageCountAdditionalText}`,
                    alignment: 'left',
                    margin: [30, -11, 0, 0],
                    style: 'footer'
                }
            ]
        },

        styles:
        {
            title:
            {
                fontSize: 30
            },
            subtitle:
            {
                fontSize: 14,
                margin: [0, 5, 0, 0]
            },
            'header-1':
            {
                fontSize: 18
            },
            'header-2':
            {
                fontSize: 16
            },
            link:
            {
                color: '#0068F8'
            },
            footer:
            {
                fontSize: 10,
                color: '#222'
            }
        }
    }

    const pdf = pdfMake.createPdf(doc)
    console.log('Generated PDF', pdf)

    return pdf
}


async function generateReportPDFAsync (report)
{
    console.log(`Generating pdf for report ${JSON.stringify(report)}`)
    
    const title = `Signalement ${report.id}, ${DateUtils.convertDateString(report.date, 'iso', 'fr', '-')}, ${report.reason}`
    let senderInformation = { text: 'L\'envoyeur a préféré rester anonyme' }
    if (!report.isAnonymous)
    {
        senderInformation =
        [
            {
                text: `Nom : ${report.senderLastName}`
            },
            {
                text: `Prénom : ${report.senderFirstName}`
            },
            {
                text: ['Mél : ', { text: report.senderMail, style: 'link' }],
            },
        ]
    }
    let senderGeolocationLink
    if (!StringUtils.isEmptyString(report.geolocation))
    {
        senderGeolocationLink =
        {
            text: 'Afficher la géolocalisation de l\'envoyeur',
            link: GeolocationUtils.generateGoogleMapLinkFromCoords(report.geolocation),
            style: 'link'
        }
    }
    let reportPicture
    if (!StringUtils.isEmptyString(report.pictureUrl))
    {
        const imageUrl = await FileReaderUtils.srcToDataUrlAsync(report.pictureUrl)
        
        reportPicture =
        [
            {
                text: 'Afficher la photographie jointe',
                linkToPage: 2,
                style: 'link',
                margin: [0, 5, 0, 0]
            },
            {
                image: imageUrl,
                fit: [500, 700],
                alignment: 'center',
                pageBreak: 'before'
            },
            separator(10),
            {
                text: 'Photographie fournie par l\'envoyeur',
                italics: true,
                alignment: 'center'
            },
        ]
    }

    const bodyContent =
    [
        {
            text :`Signalement n°${report.id}`,
            style: 'title'
        },
        {
            text: `Rapporté le ${DateUtils.dateToString(report.date, 'iso', 'fr')}`,
            style: 'subtitle'
        },
        {
            text: `Motif : ${report.reason}`,
            style: 'header-1',
            margin: [0, 20, 0, 60]
        },
        {
            text: 'Description',
            style: 'header-2'
        },
        horizontalLine({ margin: [0, 0, 0, 10] }),
        {
            text: report.description
        },
        separator(30),
        {
            text: 'Informations concernant l\'envoyeur',
            style: 'header-2'
        },
        horizontalLine({ margin: [0, 0, 0, 10] }),
        senderInformation,
        separator(30),
        senderGeolocationLink,
        reportPicture
    ]
    const footerPageCountAdditionalText = ` ― Signalement n°${report.id}`
    
    const pdf = generatePDFDocument({title, bodyContent, footerPageCountAdditionalText})

    return pdf
}

async function generateObjectPDFAsync (object)
{
    console.log(`Generating pdf for lost object ${JSON.stringify(object)}`)
    
    const objState = object.tags.includes('found') ? 'trouvé' : 'perdu'
    const title = `Objet ${object.id}, ${DateUtils.convertDateString(object.date, 'iso', 'fr', '-')}, ${objState}`
    const senderInformation =
    [
        {
            text: `Nom : ${object.sender.lastName}`
        },
        {
            text: `Prénom : ${object.sender.firstName}`
        },
        {
            text: ['Mél : ', { text: object.sender.email, style: 'link' }],
        },
        {
            text: ['Tél : ', { text: object.sender.phone, style: 'link' }],
        },
    ]
    let senderGeolocationLink
    if (!StringUtils.isEmptyString(object.geolocation))
    {
        senderGeolocationLink =
        {
            text: 'Afficher la géolocalisation de l\'objet',
            link: GeolocationUtils.generateGoogleMapLinkFromCoords(object.geolocation),
            style: 'link'
        }
    }
    let reportPicture
    if (!StringUtils.isEmptyString(object.pictureUrl))
    {
        const imageUrl = await FileReaderUtils.srcToDataUrlAsync(object.pictureUrl)
        
        reportPicture =
        [
            {
                text: 'Afficher la photographie jointe',
                linkToPage: 2,
                style: 'link',
                margin: [0, 5, 0, 0]
            },
            {
                image: imageUrl,
                fit: [500, 700],
                alignment: 'center',
                pageBreak: 'before'
            },
            separator(10),
            {
                text: 'Photographie fournie par l\'envoyeur',
                italics: true,
                alignment: 'center'
            },
        ]
    }

    const bodyContent =
    [
        {
            text :`Objet n°${object.id}`,
            style: 'title'
        },
        {
            text: `Rapporté le ${DateUtils.dateToString(object.date, 'iso', 'fr')}`,
            style: 'subtitle'
        },
        {
            text: `Déclaré comme : ${objState}`,
            style: 'header-1',
            margin: [0, 20, 0, 60]
        },
        {
            text: 'Description',
            style: 'header-2'
        },
        horizontalLine({ margin: [0, 0, 0, 10] }),
        {
            text: object.description
        },
        separator(30),
        {
            text: 'Informations concernant l\'envoyeur',
            style: 'header-2'
        },
        horizontalLine({ margin: [0, 0, 0, 10] }),
        senderInformation,
        separator(30),
        senderGeolocationLink,
        reportPicture
    ]
    const footerPageCountAdditionalText = ` ― Objet n°${object.id}`
    
    const pdf = generatePDFDocument({title, bodyContent, footerPageCountAdditionalText})

    return pdf
}

export default { generateReportPDFAsync, generateObjectPDFAsync }


// WEBPACK FOOTER //
// ./src/utils/PDFUtils.js